/**
 * components/import/UnlinkedTransactionsCard.jsx — v3.19.2
 *
 * Repairs the "it said committed but nothing was imported" case: transactions
 * that reached the ledger with no account attached, because the commit ran
 * before an account was mapped. The commit endpoint now refuses that write;
 * this card cleans up rows the old behaviour already created.
 *
 * Renders nothing at all when there is nothing to repair.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Link2, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

async function token() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

function fmtRange(g) {
  if (!g.first_date) return null;
  if (g.first_date === g.last_date) return g.first_date;
  return `${g.first_date} → ${g.last_date}`;
}

export default function UnlinkedTransactionsCard() {
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [choice, setChoice] = useState({});
  const [busyJob, setBusyJob] = useState(null);

  const load = useCallback(async () => {
    const t = await token();
    if (!t) return;
    try {
      const [res, acc] = await Promise.all([
        axios.get(`${API}/api/import/unlinked-transactions`, { headers: { Authorization: `Bearer ${t}` } }),
        supabase.from('accounts').select('id, name, type, last4, currency').order('created_at', { ascending: false }),
      ]);
      setData(res.data || null);
      setAccounts(acc.data || []);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const linkGroup = async (g) => {
    const accountId = choice[g.source_job_id] || g.suggested_account_id;
    if (!accountId) {
      toast.error('Choose the account these transactions belong to.');
      return;
    }
    setBusyJob(g.source_job_id);
    try {
      const t = await token();
      const res = await axios.post(
        `${API}/api/import/unlinked-transactions/link`,
        { source_job_id: g.source_job_id, account_id: accountId },
        { headers: { Authorization: `Bearer ${t}` }, timeout: 120000 },
      );
      const { linked = 0, skipped = [] } = res.data || {};
      toast.success(
        `Linked ${linked} transaction${linked === 1 ? '' : 's'}${skipped.length ? `, skipped ${skipped.length} already in that account` : ''}.`,
      );
      window.dispatchEvent(new Event('accounts:changed'));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || 'Could not link those transactions.');
    } finally {
      setBusyJob(null);
    }
  };

  if (!data || !data.total) return null;

  return (
    <Card className="rounded-2xl border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5" data-testid="unlinked-transactions-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--gold))]" />
          {data.total} imported transaction{data.total === 1 ? '' : 's'} aren&rsquo;t linked to an account
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          These were imported before an account was chosen, so they sit in your ledger without
          belonging to anything — which is why the statement looked like it imported nothing.
          Pick the right account and we&rsquo;ll attach them.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.groups.map((g) => {
          const det = g.detected || {};
          const detLine = [det.bank_name, det.last4 ? `••${det.last4}` : null, det.currency]
            .filter(Boolean).join(' · ');
          return (
            <div
              key={g.source_job_id}
              className="rounded-xl border border-border/60 bg-card px-3 py-2.5"
              data-testid={`unlinked-group-${g.source_job_id}`}
            >
              <div className="text-sm font-medium truncate">{g.filename || 'Statement'}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {g.count} transaction{g.count === 1 ? '' : 's'}
                {fmtRange(g) ? ` · ${fmtRange(g)}` : ''}
                {detLine ? ` · statement says ${detLine}` : ''}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Select
                  value={choice[g.source_job_id] || g.suggested_account_id || undefined}
                  onValueChange={(v) => setChoice((c) => ({ ...c, [g.source_job_id]: v }))}
                >
                  <SelectTrigger
                    className="h-8 w-[240px] rounded-xl text-xs"
                    data-testid={`unlinked-account-select-${g.source_job_id}`}
                  >
                    <SelectValue placeholder="Choose an account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}{a.last4 ? ` ••${a.last4}` : ''} · {(a.currency || '').toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="rounded-xl h-8"
                  disabled={busyJob === g.source_job_id}
                  onClick={() => linkGroup(g)}
                  data-testid={`unlinked-link-btn-${g.source_job_id}`}
                >
                  {busyJob === g.source_job_id
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Linking…</>
                    : <><Link2 className="h-3.5 w-3.5 mr-1.5" /> Attach to this account</>}
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
